import { useEffect, useRef } from 'react';

/**
 * When `fileId` changes, if the media was playing just before the change,
 * attach a one-shot `canplay` listener that resumes playback.
 * This preserves the user's playback state across file switches within
 * a single column.
 */
export function useAutoResume(
  mediaRef: React.RefObject<HTMLMediaElement | null>,
  fileId: number,
) {
  const prevIdRef = useRef(fileId);
  const wasPlayingRef = useRef(false);

  // Snapshot playing state synchronously when fileId is about to change.
  // Running this during render (not in an effect) ensures we capture
  // the playing state BEFORE React reconciles the new <source> children.
  if (prevIdRef.current !== fileId) {
    wasPlayingRef.current = !!mediaRef.current && !mediaRef.current.paused;
    prevIdRef.current = fileId;
  }

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !wasPlayingRef.current) return;
    wasPlayingRef.current = false; // consume so a stray rerender doesn't re-fire
    const handler = () => {
      el.play().catch((err) => console.warn('useAutoResume: play() rejected', err));
    };
    el.addEventListener('canplay', handler, { once: true });
    return () => el.removeEventListener('canplay', handler);
  }, [fileId, mediaRef]);
}
